import React, { ComponentPropsWithoutRef, useEffect, useMemo, useState } from 'react'
import { Descendant, Text } from 'slate'

import {
  VariableResultCommon,
  VariableResultGlobalVariable,
  VariableResultIcon,
  VariableTextFeatureFlag,
} from '@makyoapp/makyo-common'
import { MaterialIcon, MaterialIconProps, renderTextWithEmojiSpans } from '@makyoapp/makyo-tw-ui'

import { IAppLayer } from '@/core/app-builder/app/layer'
import { useGlobalVariableValues } from '@/core/app-builder/global-variables'
import { parsePageContent, parsePageTitle, parseVariableText } from '@/core/app-builder/parser/variable-text'
import cookies from '@/lib/cookies'
import { useAppAuthContext } from '@/context/__project__/AppAuthContext'
import { useDbClientContext } from '@/context/__project__/DbClientContext'

import { getColBrief } from '../utils'

/**
 * CompVariableTextGlobalVariable
 */
const CompVariableTextGlobalVariable = ({ node }: { node: VariableResultGlobalVariable }) => {
  const { getVariableValue } = useGlobalVariableValues()
  const value = getVariableValue(node.value)
  return <>{value}</>
}

/**
 * CompVariableTextSingleColData
 */
const CompVariableTextSingleColData = ({ node, containerClient, tableId, rowId, textProps, iconProps }: any) => {
  const { dbClient, language, userSpecific, tablesMap } = useDbClientContext()
  const { userEmail } = useAppAuthContext()

  const tbl = 'tbl' in node && typeof node.tbl === 'string' ? node.tbl : tableId
  const table = tablesMap.get(tbl)

  const colNode = 'col' in node ? node.col : undefined
  const colBrief = getColBrief(table, colNode)

  const val = parseVariableText([node], dbClient, containerClient, userEmail, language, userSpecific, tbl, rowId)

  if (colBrief?._Type === 'icon') {
    return <MaterialIcon className='shrink-0' icon={val} {...iconProps} />
  }

  if (colBrief?._Type === 'image') {
    return <img src={val} alt={`${colBrief?._id} image`} />
  }

  return <span {...textProps}>{typeof val === 'string' ? renderTextWithEmojiSpans(val) : val}</span>
}

/**
 * CompVariablePageTitle
 */
const CompVariablePageTitle = ({ containerClient, rowId }: any) => {
  const { language, userSpecific } = useDbClientContext()

  const [parsed, setParsed] = useState('')

  useEffect(() => {
    if (!containerClient) return

    // Recompute immediately on dependency change
    setParsed(parsePageTitle(containerClient, language, userSpecific, rowId))

    // Subscribe to page (layer) updates so changes to page content trigger recomputation
    const unsubscribe = containerClient.subscribe(() => {
      setParsed(parsePageTitle(containerClient, language, userSpecific, rowId))
    })
    return () => {
      unsubscribe()
    }
  }, [containerClient, language, userSpecific, rowId])

  return <>{renderTextWithEmojiSpans(parsed)}</>
}

/**
 * CompVariablePageLabel
 */
const CompVariablePageLabel = ({ containerClient, rowId, field }: any) => {
  const { language, userSpecific } = useDbClientContext()

  const [parsed, setParsed] = useState('')

  // Subscribe to page (layer) updates so changes to page content trigger recomputation
  useEffect(() => {
    if (!containerClient) return

    setParsed(parsePageContent(containerClient, language, userSpecific, field, rowId))

    const unsubscribe = containerClient.subscribe(() => {
      setParsed(parsePageContent(containerClient, language, userSpecific, field, rowId))
    })
    return () => {
      unsubscribe()
    }
  }, [containerClient, field, language, userSpecific, rowId])

  return <>{renderTextWithEmojiSpans(parsed)}</>
}

/**
 * CompVariableTextSingle
 */
const CompVariableTextSingle = ({
  node,
  containerClient,
  tableId,
  rowId,
  iconProps,
  textProps,
  className,
}: {
  node: Descendant
  containerClient: IAppLayer
  tableId?: string
  rowId?: string
  iconProps?: MaterialIconProps
  textProps?: ComponentPropsWithoutRef<'span'>
  className?: string
}) => {
  if (Text.isText(node)) {
    return Boolean(node.text) ? (
      <span {...textProps} className={className}>
        {renderTextWithEmojiSpans(node.text)}
      </span>
    ) : (
      <>&nbsp;</>
    )
  }

  if (node.type === 'paragraph') {
    return (
      <>
        {node.children.map((child, index) => (
          <React.Fragment key={index}>
            <CompVariableTextSingle
              node={child}
              containerClient={containerClient}
              tableId={tableId}
              rowId={rowId}
              iconProps={iconProps}
              textProps={textProps}
              className={className}
            />
            {index === node.children.length - 1 && <br />}
          </React.Fragment>
        ))}
      </>
    )
  }

  if (Array.isArray(node)) {
    return (
      <>
        {node.map((n, i) => {
          return (
            <CompVariableTextSingle
              key={i}
              node={n}
              containerClient={containerClient}
              tableId={tableId}
              rowId={rowId}
              iconProps={iconProps}
              textProps={textProps}
              className={className}
            />
          )
        })}
      </>
    )
  } else {
    switch (Number(node.type)) {
      case VariableTextFeatureFlag.PageTitle:
        return <CompVariablePageTitle containerClient={containerClient} rowId={rowId} />
      case VariableTextFeatureFlag.PageLabel:
        return <CompVariablePageLabel containerClient={containerClient} rowId={rowId} field='_Label' />
      case VariableTextFeatureFlag.PageSubtitle:
        return <CompVariablePageLabel containerClient={containerClient} rowId={rowId} field='_Subtitle' />
      case VariableTextFeatureFlag.PageDescription:
        return <CompVariablePageLabel containerClient={containerClient} rowId={rowId} field='_Description' />
      case VariableTextFeatureFlag.Icon: {
        const n = node as unknown as VariableResultIcon
        return <MaterialIcon className='shrink-0' {...iconProps} icon={n.value} />
      }
      case VariableTextFeatureFlag.Image: {
        const n = node as unknown as VariableResultCommon
        return <img src={n.value} />
      }
      case VariableTextFeatureFlag.Column:
      case VariableTextFeatureFlag.CurrentRow:
      case VariableTextFeatureFlag.CurrentListItem:
      case VariableTextFeatureFlag.SingleCell:
      case VariableTextFeatureFlag.ColumnData: {
        return (
          <CompVariableTextSingleColData
            node={node}
            containerClient={containerClient}
            tableId={tableId}
            rowId={rowId}
            textProps={textProps}
            iconProps={iconProps}
          />
        )
      }
      case VariableTextFeatureFlag.DateTime: {
        const now = new Date()
        return <>{now.toLocaleString()}</>
      }
      case VariableTextFeatureFlag.ProfileData: {
        return (
          <CompVariableTextSingleColData
            node={node}
            containerClient={containerClient}
            tableId={tableId}
            textProps={textProps}
          />
        )
      }
      case VariableTextFeatureFlag.SessionID: {
        const sessionIDValue = cookies.getAppSessionId(window.appID)
        return <>{sessionIDValue}</>
      }
      case VariableTextFeatureFlag.GlobalVariable: {
        return <CompVariableTextGlobalVariable node={node as unknown as VariableResultGlobalVariable} />
      }
      default:
        return (
          <>
            {node.children.map((n, i) => {
              return (
                <CompVariableTextSingle
                  key={i}
                  node={n as Descendant}
                  containerClient={containerClient}
                  tableId={tableId}
                  iconProps={iconProps}
                  textProps={textProps}
                  className={className}
                />
              )
            })}
          </>
        )
    }
  }
}

interface CompVariableTextProps {
  nodes?: Descendant[]
  containerClient: IAppLayer
  tableId?: string
  /** @deprecated - use rowId instead */
  rowIndex?: number
  rowId?: string
  textProps?: ComponentPropsWithoutRef<'span'>
  iconProps?: MaterialIconProps
  className?: string
}

/**
 * CompVariableText
 *
 * @internal - used by other core components
 */
export const CompVariableText = ({
  nodes = [],
  containerClient,
  tableId,
  rowIndex,
  rowId,
  textProps,
  iconProps,
  className,
}: CompVariableTextProps) => {
  //
  const fixedNodes = useMemo(() => {
    if (!Array.isArray(nodes) && nodes !== null && nodes !== undefined) {
      return [nodes]
    }
    return nodes || []
  }, [nodes])

  return fixedNodes.map((v, i) => (
    <CompVariableTextSingle
      key={i}
      node={v}
      containerClient={containerClient}
      tableId={tableId}
      rowId={rowId}
      textProps={textProps}
      iconProps={iconProps}
      className={className}
    />
  ))
}
